import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import { requestMagicLink } from "../lib/api";

export default function PortalLogin() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestMagicLink(email);
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again shortly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Candidate Login" description="Sign in to the AMVI Technologies candidate portal to track your application status." path="/portal/login" />
      <PageHero
        eyebrow="Candidate Portal"
        title="Track Your Application"
        subtitle="Sign in with a secure, passwordless link. Enter your email and we'll send you a one-time sign-in link."
      />
      <section className="px-6 pb-24">
        <Reveal className="mx-auto max-w-md">
          <div className="rounded-2xl border border-white/10 bg-ink-800/50 p-8 lg:p-10" data-testid="portal-login-card">
            {sent ? (
              <div className="text-center" data-testid="portal-login-sent">
                <div className="mx-auto mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-sky-400/10 text-sky-400">
                  <Mail className="h-7 w-7" />
                </div>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-white">Check your email</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  If <span className="text-white">{email}</span> is eligible, we&apos;ve sent a secure sign-in link. It expires in 15 minutes and can be used once.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 text-sm font-medium text-sky-400 hover:text-sky-300"
                  data-testid="portal-login-again"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="portal-login-form">
                <div>
                  <label htmlFor="email" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-sky-400/70"
                    data-testid="portal-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-400 px-6 py-3.5 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-300 disabled:opacity-60"
                  data-testid="portal-email-submit"
                >
                  {loading ? "Sending..." : "Email me a sign-in link"}
                  {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
                </button>
                <p className="text-center text-xs text-slate-500">
                  Use the email address you applied with.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </>
  );
}
